import React, { useMemo, useCallback } from "react";
import { FormattedMessage } from "react-intl";
import moment from "moment";
import { Icon } from "@wfp/ui";
import { iconCaretDown, iconCaretUp } from "@wfp/icons";

import ItemSwitcher from "components/ItemSwitcher";
import ReportItem from "components/ItemSwitcher/ReportItem";

import { getIdToNoMealReasonMapping } from "data-handler/ducks/noMealReasons";
import { getSortedMonths } from "helpers/dates";

import { useSelector } from "react-redux";

import "./_dropdown.scss";
import {
  isAttendanceStoreCompleted,
  sidebarStyling,
} from "data-handler/ducks/stores";

const SchoolYearGroupDropdown = React.memo((props) => {
  const {
    schoolYears,
    setCollapsedYearOrMonth,
    currentSchoolYear,
    deliveriesAndIncidentsAndPurchasesByMonth,
    attendancesAndPlaceholders,
    reportsAndPreviewsBySchoolYear,
    type,
    byMonths = false,
    currentSchoolEnabledTHR = false,
    currentSchoolEnabledStudentAttendance = false,
  } = props;

  const dateFormatter = useCallback(
    (date) => moment(date).format("MMMM\xa0YYYY"),
    []
  );

  const idToNoMealReasonMapping = useSelector(getIdToNoMealReasonMapping);

  // Memoize attendance data per month to avoid recalculating
  const attendanceDataByMonth = useMemo(() => {
    if (type !== "attendanceAndMeals") return {};

    const cache = {};
    schoolYears.forEach((schoolYear) => {
      if (!schoolYear) return;
      cache[schoolYear.id || schoolYear.client_id] = {};
      getSortedMonths(schoolYear.months).forEach((month) => {
        cache[schoolYear.id || schoolYear.client_id][
          month
        ] = attendancesAndPlaceholders(schoolYear, month);
      });
    });
    return cache;
  }, [schoolYears, attendancesAndPlaceholders, type]);

  const sidebarItemsByMonth = useCallback(
    (schoolYear, month) => {
      switch (type) {
        case "attendanceAndMeals":
          const schoolYearKey = schoolYear.id || schoolYear.client_id;
          const attendanceData =
            attendanceDataByMonth[schoolYearKey]?.[month] || [];
          return attendanceData.map((store) => {
            return (
              <ItemSwitcher
                store={store.attendance}
                studentStore={store.studentAttendance}
                thrStore={store.thrAttendance}
                key={store.client_id}
                currentSchoolEnabledTHR={currentSchoolEnabledTHR}
                currentSchoolEnabledStudentAttendance={
                  currentSchoolEnabledStudentAttendance
                }
              />
            );
          });
        case "deliveriesAndIncidentsAndPurchases":
          return deliveriesAndIncidentsAndPurchasesByMonth(
            schoolYear.months[month]
          ).map((store) => {
            return <ItemSwitcher store={store} key={store.client_id} />;
          });
        default:
          return null;
      }
    },
    [
      type,
      attendanceDataByMonth,
      currentSchoolEnabledTHR,
      currentSchoolEnabledStudentAttendance,
      deliveriesAndIncidentsAndPurchasesByMonth,
    ]
  );

  const sidebarItemsByYear = useCallback(
    (schoolYear) => {
      switch (type) {
        case "reports":
          return reportsAndPreviewsBySchoolYear(schoolYear).map((report) => (
            <ReportItem report={report} key={report.client_id} />
          ));
        default:
          return null;
      }
    },
    [type, reportsAndPreviewsBySchoolYear]
  );

  const hasOpenedOrRejectedReports = useCallback(
    (schoolYear) => {
      const openedOrRejectedReport = reportsAndPreviewsBySchoolYear(
        schoolYear
      ).find(
        (report) => report.state === "open" || report.state === "rejected"
      );
      if (openedOrRejectedReport) {
        return true;
      }
      return false;
    },
    [reportsAndPreviewsBySchoolYear]
  );

  const checkSchoolYears = useCallback((schoolYear, currentSchoolYear) => {
    if (!schoolYear.id && !currentSchoolYear?.id) {
      // We use the client id if the school years have not been synced
      return schoolYear.client_id === currentSchoolYear?.client_id;
    }
    return schoolYear.id === currentSchoolYear?.id;
  }, []);

  // Memoize completion status per month
  const monthCompletionStatus = useMemo(() => {
    if (type !== "attendanceAndMeals") return {};

    const cache = {};
    schoolYears.forEach((schoolYear) => {
      if (!schoolYear) return;
      const schoolYearKey = schoolYear.id || schoolYear.client_id;
      cache[schoolYearKey] = {};

      getSortedMonths(schoolYear.months).forEach((month) => {
        const attendanceData =
          attendanceDataByMonth[schoolYearKey]?.[month] || [];
        const completed = attendanceData.every((store) =>
          isAttendanceStoreCompleted(
            store.attendance,
            store.studentAttendance,
            store.currentSchoolEnabledStudentAttendance,
            idToNoMealReasonMapping
          )
        );
        cache[schoolYearKey][month] = completed;
      });
    });
    return cache;
  }, [schoolYears, attendanceDataByMonth, idToNoMealReasonMapping, type]);

  const isMonthCompleted = useCallback(
    (schoolYear, month) => {
      const schoolYearKey = schoolYear.id || schoolYear.client_id;
      const completed = monthCompletionStatus[schoolYearKey]?.[month] ?? false;
      return completed ? sidebarStyling.complete : sidebarStyling.incomplete;
    },
    [monthCompletionStatus]
  );

  const isYearCompleted = useCallback(
    (schoolYear) => {
      const schoolYearKey = schoolYear.id || schoolYear.client_id;
      const monthStatuses = monthCompletionStatus[schoolYearKey];
      if (!monthStatuses) return sidebarStyling.incomplete;

      const allCompleted = Object.values(monthStatuses).every(
        (status) => status === true
      );
      return allCompleted ? sidebarStyling.complete : sidebarStyling.incomplete;
    },
    [monthCompletionStatus]
  );

  const hasOpenedOrRejectedReportsStyling = useCallback(
    (schoolYear) => {
      return !hasOpenedOrRejectedReports(schoolYear)
        ? sidebarStyling.complete
        : sidebarStyling.incomplete;
    },
    [hasOpenedOrRejectedReports]
  );

  const handleYearToggle = useCallback(
    (schoolYear) => {
      setCollapsedYearOrMonth(schoolYear);
    },
    [setCollapsedYearOrMonth]
  );

  const handleMonthToggle = useCallback(
    (schoolYear, month) => {
      setCollapsedYearOrMonth(schoolYear, month);
    },
    [setCollapsedYearOrMonth]
  );

  return (
    <>
      {schoolYears.map(
        (schoolYear) =>
          schoolYear && (
            <div key={schoolYear.id} className="school-year-group">
              <div
                tabIndex={0}
                role="link"
                className="school-year-dropdown"
                onClick={() => handleYearToggle(schoolYear)}
                onKeyDown={() => handleYearToggle(schoolYear)}
                style={
                  type === "attendanceAndMeals" && schoolYear?.collapsed
                    ? isYearCompleted(schoolYear)
                    : type === "reports"
                    ? hasOpenedOrRejectedReportsStyling(schoolYear)
                    : {}
                }
              >
                <label className="wfp--label">
                  {checkSchoolYears(schoolYear, currentSchoolYear) ? (
                    <FormattedMessage
                      id="schools.currentSchoolYear"
                      defaultMessage="Current school year ({start_date} to {end_date})"
                      values={{
                        start_date: dateFormatter(schoolYear.starts_on),
                        end_date: dateFormatter(schoolYear.ends_on),
                      }}
                    />
                  ) : (
                    <FormattedMessage
                      id="schools.schoolYear"
                      defaultMessage="School year ({start_date} to {end_date})"
                      values={{
                        start_date: dateFormatter(schoolYear.starts_on),
                        end_date: dateFormatter(schoolYear.ends_on),
                      }}
                    />
                  )}
                </label>
                <Icon
                  icon={schoolYear.collapsed ? iconCaretDown : iconCaretUp}
                  className="icon-dropdown"
                />
              </div>
              <div className="school-year-subgroup">
                {schoolYear.collapsed
                  ? null
                  : !byMonths
                  ? sidebarItemsByYear(schoolYear)
                  : getSortedMonths(schoolYear.months).map((month) => {
                      const currentSidebarByMonth = sidebarItemsByMonth(
                        schoolYear,
                        month
                      );

                      return currentSidebarByMonth.length > 0 ? (
                        <div
                          key={schoolYear.months[month].starts_on}
                          className="month-group"
                        >
                          <div
                            className="month-dropdown"
                            onClick={() => handleMonthToggle(schoolYear, month)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={() => handleYearToggle(schoolYear)}
                            style={
                              schoolYear.months[month].collapsed &&
                              type === "attendanceAndMeals"
                                ? isMonthCompleted(schoolYear, month)
                                : {}
                            }
                          >
                            <label
                              className="wfp--label"
                              htmlFor="school-month"
                            >
                              <FormattedMessage
                                id="schools.monthName"
                                defaultMessage=" - {month}"
                                values={{
                                  month: dateFormatter(
                                    schoolYear.months[month].starts_on
                                  ),
                                }}
                              />
                            </label>
                            <Icon
                              icon={
                                schoolYear.months[month].collapsed
                                  ? iconCaretDown
                                  : iconCaretUp
                              }
                              className="icon-dropdown"
                            />
                          </div>
                          <div className="month-subgroup">
                            {schoolYear.months[month].collapsed
                              ? null
                              : currentSidebarByMonth}
                          </div>
                        </div>
                      ) : null;
                    })}
              </div>
            </div>
          )
      )}
    </>
  );
});

export default SchoolYearGroupDropdown;
